import React, { FC, useState, useEffect } from 'react';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import {
    Admin,
    CustomRoutes,
    Resource,
    localStorageStore,
    useStore,
    StoreContextProvider,
    EditGuesser,
    ListGuesser,
} from 'react-admin';
import { Route } from 'react-router';
import jwtAuthProvider from './common/jwtAuthProvider';
import { Dashboard } from './screens/dashboard';
import englishMessages from './common/i18n/en';
import { Layout, Login } from './layout';
import { themes, ThemeName } from './common/themes/themes';
import VSDataProvider from "./common/VSDataProvider";
import { APIUrl } from './common/apiClient';
import users from './screens/users';
import subjects from './screens/subject';
import academicContent from './screens/academic-content';
import schools from './screens/school';
import SchoolMembers from './screens/school-member/SchoolMembers';
import Migrations from './screens/migrations/Migrations';
import academicYears from './screens/academic-year';
import classSections from './screens/class-section';
import schoolTeachers from './screens/school-teacher';
import schoolStudents from './screens/school-student';
import rosterImport from './screens/roster-import';
import schoolTests from './screens/school-test';
import assignments from './screens/assignment';
import "./App.css";
import LoginNew from './layout/LoginNew';
import { MyDataProvider } from './common/MyDataProvider';
import exam from './screens/exam';
import chapter from './screens/chapter';
import topic from './screens/topic';
import question from './screens/question';
import questionAnalysis from './screens/question-analysis';
import studentPerformance from './screens/student-performance';
import UserQuizStatistics from './screens/reports';
import flaggedQuestions from './screens/flagged-questions';
import quiz from './screens/quiz';
import wiziQuiz from './screens/wizi-quiz';
import wiziQuestion from './screens/wizi-question';
import { WiziQuizQuestionManage } from './screens/wizi-quiz-question';
import NotificationSend from './screens/notifications/NotificationSend';
import { SolutionGeneratorScreen } from './screens/solution-generator';
import subscriptionPlans from './screens/subscription-plans';
import userSubscriptions from './screens/user-subscriptions';
import SubscriptionAnalytics from './screens/subscription-analytics';
import subscriptionTransactions from './screens/subscription-transactions';
import subscriptionFeatures from './screens/subscription-features';
import PlanFeaturesEdit from './screens/plan-features/PlanFeaturesEdit';
import { MathJaxProvider } from './components/MathJaxRenderer';
import { QuizQuestionsList } from './screens/quiz/QuizQuestionsList';
import NotificationContextManager from './screens/notification-context';
import notificationPreview from './screens/notification-preview';
import BatchNotificationSender from './screens/batch-notification-sender/BatchNotificationSender';

const i18nProvider = polyglotI18nProvider(
    locale => {
        if (locale === 'fr') {
            return import('./common/i18n/fr').then(messages => messages.default);
        }

        // Always fallback on english
        return englishMessages;
    },
    'en',
    [
        { locale: 'en', name: 'English' },
        { locale: 'fr', name: 'Français' },
    ]
);

const store = localStorageStore(undefined, 'VS-Framework');

const App = () => {
    const [themeName] = useStore<ThemeName>('themeName', 'radiant');
    const lightTheme = themes.find(theme => theme.name === themeName)?.light;
    const darkTheme = themes.find(theme => theme.name === themeName)?.dark;

    const [dataProvider, setDataProvider]:any = useState({});

    useEffect(() => {
      let provider = MyDataProvider(APIUrl);
      setDataProvider(provider);
  
    }, []);

    return (
        <Admin
            title=""
            dataProvider={dataProvider}
            store={store}
            authProvider={jwtAuthProvider}
            dashboard={Dashboard}
            loginPage={LoginNew}
            layout={Layout}
            i18nProvider={i18nProvider}
            disableTelemetry
            lightTheme={lightTheme}
            darkTheme={darkTheme}
            defaultTheme="light"
        >
            <Resource name="user" {...users} />
            <Resource name="subject" {...subjects} />
            <Resource name="school" {...schools} />
            {/*
              School-owned resources. These live in the SCHOOL's database, not
              in Master, and every endpoint behind them refuses a caller with no
              school binding. They are registered for everyone because
              react-admin needs the resource to exist before a link to it can
              resolve; Menu.tsx is what decides who is shown the links, and
              require_school_role() on the API is what actually enforces it.
            */}
            <Resource name="academic-year" {...academicYears} />
            <Resource name="class-section" {...classSections} />
            <Resource name="school-teacher" {...schoolTeachers} />
            <Resource name="school-student" {...schoolStudents} />
            <Resource name="roster-import" {...rosterImport} />
            <Resource name="school-test" {...schoolTests} />
            <Resource name="assignment" {...assignments} />
            <Resource name="role" list={ListGuesser} edit={EditGuesser} />
            <Resource name="exam" {...exam} />
            <Resource name="chapter" {...chapter} />
            <Resource name="topic" {...topic} />
            <Resource name="question" {...question} />
            <Resource name="quiz" {...quiz} />
            <Resource name="wizi-quiz" {...wiziQuiz} />
            <Resource name="wizi-question" {...wiziQuestion} />
            <Resource name="academic-content" {...academicContent} />
            {/*
              Lookup-only resources, registered so ReferenceInput can resolve
              them. Neither has a screen of its own: class-group and
              content-type are seeded taxonomy, edited by migration rather than
              by hand -- content_type.media in particular decides which viewer
              the student apps open, so changing it under live rows would
              strand their URLs.
            */}
            <Resource name="class-group" />
            <Resource name="content-type" />
            <Resource name="question-analysis" {...questionAnalysis} />
            <Resource name="student-performance" {...studentPerformance} />
            <Resource name="flagged-questions" {...flaggedQuestions} />
            <Resource name="subscription-plans" {...subscriptionPlans} />
            <Resource name="user-subscriptions" {...userSubscriptions} />
            <Resource name="subscription-transactions" {...subscriptionTransactions} />
            <Resource name="subscription-features" {...subscriptionFeatures} />
            <Resource name="notification-preview" {...notificationPreview} />
            <CustomRoutes>
                {/*
                  WiZi staff adding a school's principal. Not a Resource: it is
                  not CRUD over one collection, it picks the school first --
                  which every school-scoped screen is forbidden from doing and
                  this one has to. /api/school_member is behind
                  require_super_admin() for the same reason.
                */}
                <Route path="/school-member" element={<SchoolMembers />} />
                {/*
                  The schema migration runner. Not a Resource either: it is one
                  status board over two lanes and a fleet of databases, not a
                  collection. /api/migration is behind require_super_admin().
                */}
                <Route path="/migrations" element={<Migrations />} />
                <Route path="/reports/user-quiz-statistics" element={<UserQuizStatistics />} />
                <Route path="/notifications/send" element={<NotificationSend />} />
                <Route path="/notifications/context-manager" element={<NotificationContextManager />} />
                <Route path="/notifications/batch-sender" element={<BatchNotificationSender />} />
                <Route path="/ai/solution-generator" element={<SolutionGeneratorScreen />} />
                <Route path="/subscription-analytics" element={<SubscriptionAnalytics />} />
                <Route path="/plan-features/:planId" element={<PlanFeaturesEdit />} />
                <Route path="/wizi-quiz-questions" element={<WiziQuizQuestionManage />} />
                <Route path="/quiz/:quizId/questions" element={<QuizQuestionsList />} />
            </CustomRoutes>
            
        </Admin>
    );
};

const AppWrapper = () => (
    <StoreContextProvider value={store}>
        <MathJaxProvider>
            <App />
        </MathJaxProvider>
    </StoreContextProvider>
);

export default AppWrapper;
